import { useRef, useState } from 'react'
import { Upload, FolderOpen, FileText, X, AlertCircle } from 'lucide-react'
import { useTranslation } from '../../../i18n/LanguageContext'

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

const CustomFileField = ({ field, value, onChange, error, aispaceColor, onSharePoint }) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  // Tracks the last clicked card so we can render the active highlight, just
  // like the default Upload Method UI in the Create Project modal.
  const [activeMethod, setActiveMethod] = useState(null)

  let cfg = {}
  try { cfg = field.options ? JSON.parse(field.options) : {} } catch { cfg = {} }
  const source = cfg.uploadSource || 'computer'
  const showComputer = source === 'computer' || source === 'both'
  const showSharePoint = source === 'sharepoint' || source === 'both'

  const files = Array.isArray(value) ? value : []

  const handleSelect = (e) => {
    const chosen = Array.from(e.target.files || [])
    if (chosen.length > 0) onChange([...files, ...chosen])
    e.target.value = ''
  }

  const removeAt = (idx) => onChange(files.filter((_, i) => i !== idx))

  return (
    <div>
      <label className="block text-[14px] font-semibold text-[#111827] mb-3">
        {field.field_label}
        {field.required && <span className="text-[#EF4444] ml-1">*</span>}
      </label>

      <div className="space-y-3">
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleSelect}
          className="hidden"
        />

        {/* Upload from device — same card style as default modal */}
        {showComputer && (
          <button
            type="button"
            onClick={() => {
              setActiveMethod('device')
              inputRef.current?.click()
            }}
            className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
            style={{
              border: activeMethod === 'device' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
              backgroundColor: activeMethod === 'device' ? `${aispaceColor}15` : 'transparent',
            }}
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <Upload size={22} className="text-[#6B7280]" />
            </div>
            <div className="text-left">
              <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.uploadFromDevice')}</p>
              <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.uploadFromDeviceHint')}</p>
            </div>
          </button>
        )}

        {/* Connect SharePoint — same card style */}
        {showSharePoint && (
          <button
            type="button"
            onClick={() => {
              setActiveMethod('sharepoint')
              if (onSharePoint) onSharePoint()
            }}
            className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
            style={{
              border: activeMethod === 'sharepoint' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
              backgroundColor: activeMethod === 'sharepoint' ? `${aispaceColor}15` : 'transparent',
            }}
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <FolderOpen size={22} className="text-[#6B7280]" />
            </div>
            <div className="text-left">
              <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.connectSharePoint')}</p>
              <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.connectSharePointHint')}</p>
            </div>
          </button>
        )}
      </div>

      {/* Uploaded Files List — same pill rows as default modal */}
      {files.length > 0 && (
        <div className="space-y-2 mt-3">
          {files.map((file, idx) => {
            const isSP = !(file instanceof File)
            return (
              <div key={`${file.name}-${idx}`} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={20} className="text-[#6B7280] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-[#374151] truncate max-w-[280px]">{file.name}</p>
                    {isSP ? (
                      <p className="text-[12px] text-blue-600">SharePoint</p>
                    ) : (
                      <p className="text-[12px] text-[#9CA3AF]">{formatBytes(file.size || 0)}</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="p-1 hover:bg-[#FEE2E2] rounded transition-colors"
                >
                  <X size={16} className="text-[#EF4444]" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {field.helper_text && !error && (
        <p className="text-[12px] text-[#6B7280] mt-2">{field.helper_text}</p>
      )}
      {error && (
        <div className="flex items-start gap-1 text-[12px] text-[#EF4444] mt-2">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export default CustomFileField
